import { useEffect, useRef } from 'react';
import './CustomCursor.css';

const TRAIL_COUNT = 5;

const CustomCursor = () => {
  const cursorRef  = useRef(null);
  const trailRefs  = useRef([]);
  const mousePos   = useRef({ x: -200, y: -200 });
  const trailPos   = useRef(Array.from({ length: TRAIL_COUNT }, () => ({ x: -200, y: -200 })));
  const rafId      = useRef(null);
  const stateRef   = useRef({
    clicked: false, linkHovered: false,
    navHovered: false, socialHovered: false, hidden: false,
  });

  useEffect(() => {
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;

    const cursor = cursorRef.current;
    if (!cursor) return;

    // Signal to CSS that the custom cursor is live — only now hide the system pointer.
    document.body.classList.add('custom-cursor-active');

    /* ─ apply CSS classes based on state ─ */
    const sync = () => {
      const s = stateRef.current;
      cursor.className = [
        'cursor',
        s.hidden        && 'cursor--hidden',
        s.clicked       && 'cursor--clicked',
        s.linkHovered   && 'cursor--link-hovered',
        s.navHovered    && 'cursor--nav-hovered',
        s.socialHovered && 'cursor--social-hovered',
      ].filter(Boolean).join(' ');
    };

    /* ─ RAF loop: lerp trail positions, write to DOM directly ─ */
    const tick = () => {
      const { x, y } = mousePos.current;
      cursor.style.left = x + 'px';
      cursor.style.top  = y + 'px';

      let px = x, py = y;
      trailPos.current.forEach((tp, i) => {
        tp.x += (px - tp.x) * 0.3;
        tp.y += (py - tp.y) * 0.3;
        px = tp.x; py = tp.y;
        const el = trailRefs.current[i];
        if (el) {
          el.style.left = tp.x + 'px';
          el.style.top  = tp.y + 'px';
        }
      });
      rafId.current = requestAnimationFrame(tick);
    };

    /* ─ event handlers ─ */
    const onMove  = (e) => { mousePos.current = { x: e.clientX, y: e.clientY }; };
    const onDown  = () => { stateRef.current.clicked = true;  sync(); };
    const onUp    = () => { stateRef.current.clicked = false; sync(); };
    const onLeave = () => { stateRef.current.hidden  = true;  sync(); };
    const onEnter = () => { stateRef.current.hidden  = false; sync(); };

    /* use event delegation instead of attaching to every element */
    const onOver = (e) => {
      if (e.target.closest('a, button, .link'))          { stateRef.current.linkHovered   = true;  sync(); }
      if (e.target.closest('#header .nav-link'))         { stateRef.current.navHovered    = true;  sync(); }
      if (e.target.closest('#header .social-bar a'))     { stateRef.current.socialHovered = true;  sync(); }
    };
    const onOut = (e) => {
      if (e.target.closest('a, button, .link'))          { stateRef.current.linkHovered   = false; sync(); }
      if (e.target.closest('#header .nav-link'))         { stateRef.current.navHovered    = false; sync(); }
      if (e.target.closest('#header .social-bar a'))     { stateRef.current.socialHovered = false; sync(); }
    };

    document.addEventListener('mousemove',  onMove,  { passive: true });
    document.addEventListener('mouseenter', onEnter);
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mousedown',  onDown);
    document.addEventListener('mouseup',    onUp);
    document.addEventListener('mouseover',  onOver);
    document.addEventListener('mouseout',   onOut);
    rafId.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId.current);
      document.body.classList.remove('custom-cursor-active');
      document.removeEventListener('mousemove',  onMove);
      document.removeEventListener('mouseenter', onEnter);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mousedown',  onDown);
      document.removeEventListener('mouseup',    onUp);
      document.removeEventListener('mouseover',  onOver);
      document.removeEventListener('mouseout',   onOut);
    };
  }, []);

  // Always render the cursor nodes so server and client markup match (hydration).
  // On touch devices the effect no-ops and the CSS (@media hover:hover) hides them.
  return (
    <>
      <div ref={cursorRef} className="cursor" />
      {Array.from({ length: TRAIL_COUNT }, (_, i) => (
        <div
          key={i}
          ref={el => { trailRefs.current[i] = el; }}
          className="cursor-trail"
          style={{
            width:   (10 - i * 1.5) + 'px',
            height:  (10 - i * 1.5) + 'px',
            opacity: 1 - i * 0.18,
          }}
        />
      ))}
    </>
  );
};

export default CustomCursor;